import { BLOOM_SECTION_ID } from '@/components/home/components/bloom-section/bloom-section.model'
import { clamp01, lerp, smoothstep01 } from '@/lib/utils/math'
import { splitIntoChars, splitIntoWords } from '@/lib/utils/split-text'
import { useSceneStore } from '@/stores/scene-store'

const EYEBROW_TEXT = 'Floricultura-ateliê'
const WORDMARK_TEXT = 'Flora'
const WORDMARK_ARIA_LABEL = 'Flora Ateliê'
const SLOGAN_TEXT = 'O que você sente, em flor.'
const HINT_TEXT = 'role para florescer'
const CATALOG_ACTION_LABEL = 'Ver o catálogo'
const CONTACT_ACTION_LABEL = 'Falar por e-mail'
const ORIENTATION_TEXT = 'Buquês autorais · entrega em toda a cidade'
const TRIGGER_TEXT = 'Encomenda direto pelo e-mail do ateliê — sem cadastro.'
const ORIENTATION_REVEAL_DELAY = '0.55s'

const WORDMARK_REVEAL_START = 0.9
const WORDMARK_REVEAL_STEP = 0.05
const SLOGAN_REVEAL_START = 1.35
const SLOGAN_REVEAL_STEP = 0.014

const WORDMARK_CHARS = splitIntoChars(WORDMARK_TEXT).map((char, index) => ({
  ...char,
  delay: `${(WORDMARK_REVEAL_START + index * WORDMARK_REVEAL_STEP).toFixed(3)}s`,
}))

let sloganOrder = 0
const SLOGAN_WORDS = splitIntoWords(SLOGAN_TEXT).map((word) => ({
  value: word.value,
  key: word.key,
  chars: word.chars.map((char) => {
    const delay = `${(SLOGAN_REVEAL_START + sloganOrder * SLOGAN_REVEAL_STEP).toFixed(3)}s`
    sloganOrder += 1
    return { ...char, delay }
  }),
}))

const ACTIONS_REVEAL_BUFFER = 0.35
const ACTIONS_REVEAL_DELAY = `${(SLOGAN_REVEAL_START + sloganOrder * SLOGAN_REVEAL_STEP + ACTIONS_REVEAL_BUFFER).toFixed(3)}s`

const HINT_FADE_PROGRESS = 0.32
const EXIT_START = 0.72

function scrollToBloomSection(): void {
  document.getElementById(BLOOM_SECTION_ID)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

export interface HeroSectionModelProps {
  progress: number
}

export function useHeroSectionModel({ progress }: HeroSectionModelProps) {
  const isSceneReady = useSceneStore((state) => state.sceneReady)
  const exitAmount = smoothstep01(
    clamp01((progress - EXIT_START) / (1 - EXIT_START)),
  )

  return {
    eyebrowText: EYEBROW_TEXT,
    wordmarkAriaLabel: WORDMARK_ARIA_LABEL,
    wordmarkChars: WORDMARK_CHARS,
    sloganText: SLOGAN_TEXT,
    sloganWords: SLOGAN_WORDS,
    hintText: HINT_TEXT,
    catalogActionLabel: CATALOG_ACTION_LABEL,
    contactActionLabel: CONTACT_ACTION_LABEL,
    orientationText: ORIENTATION_TEXT,
    orientationRevealDelay: ORIENTATION_REVEAL_DELAY,
    triggerText: TRIGGER_TEXT,
    onContinueClick: scrollToBloomSection,
    isRevealed: isSceneReady,
    actionsRevealDelay: ACTIONS_REVEAL_DELAY,
    isHintVisible: isSceneReady && progress < HINT_FADE_PROGRESS,
    stageInert: progress >= 1 || !isSceneReady,
    stageStyle: {
      opacity: Math.round((1 - exitAmount) * 1000) / 1000,
      transform: `translate3d(0, ${lerp(0, -48, exitAmount).toFixed(2)}px, 0)`,
    },
  }
}
